import { useSelector } from 'react-redux'

import type { AlternativeOptions } from '@features/recipe-tile'
import type { Recipe } from '@library/menu-service'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { useIsMenuExperienceRecipeDetailEnabled } from 'routes/Menu/components/Detail/hooks/useIsMenuExperienceRecipeDetailEnabled'
import { getBasketDate, getBasketMenuUuid, getNumPortions } from 'selectors/basket'

import { useRecipeOptionsExperience } from './useRecipeOptionsExperience'
import { useRecipeOptionsV3 } from './useRecipeOptionsV3'

export const useRecipeOptions = (
  mainRecipe: Recipe,
  shouldFetchOptions: boolean,
): {
  isLoadingOptions: boolean
  alternativeOptions: AlternativeOptions
} => {
  const { basketHasRecipe } = useBasketRecipes()
  const isMenuExperienceRecipeDetailEnabled = useIsMenuExperienceRecipeDetailEnabled()

  const numPortions = useSelector(getNumPortions)
  const deliveryDate = useSelector(getBasketDate)
  const menuId = useSelector(getBasketMenuUuid)
  const collectionId = useCurrentCollectionId()
  const collection = useCollection(collectionId)

  const baseRecipe =
    collection?.recipes.find((recipe) => recipe.id === mainRecipe.id) ||
    collection?.recipes.find((recipe) =>
      recipe.options.some((option) => option.id === mainRecipe.id),
    )

  const defaultOptionsType = baseRecipe?.optionsType || 'none'

  const v3Result = useRecipeOptionsV3({
    mainRecipe,
    shouldFetchOptions: shouldFetchOptions && !isMenuExperienceRecipeDetailEnabled,
    menuId,
    numPortions,
    deliveryDate,
    collectionId,
    defaultOptionsType,
    basketHasRecipe,
  })

  const experienceResult = useRecipeOptionsExperience({
    mainRecipe,
    shouldFetchOptions: shouldFetchOptions && isMenuExperienceRecipeDetailEnabled,
    menuId,
    numPortions,
    deliveryDate,
    collectionId,
    defaultOptionsType,
    basketHasRecipe,
  })

  return isMenuExperienceRecipeDetailEnabled ? experienceResult : v3Result
}
